import { CardContent, CardFooter } from "@components/ui/card";
import { useWebsiteChatIsLoadingHistory, useWebsiteChatSubmit } from "@hooks/website";
import { useConsoleErrors } from "@lib/preview";
import WebsiteChatInput from "./chat/WebsiteChatInput";
import WebsiteChatMessages from "./chat/WebsiteChatMessages";
import { Chat, useChatIsStreaming } from "@launch10/use-langgraph-stream";
import { RECOMMENDATION_PANELS,
  GenericSubmission, recommendationIconFor } from "@components/chat/RecommendationPanels";
import { recommendationsForSurface } from "@lib/recommendationsRegistry";
import { twMerge } from "tailwind-merge";
import { useCallback } from "react";
import { useProjectUuid } from "@stores/projectStore";
import { usePreviewStore } from "@stores/previewStore";
import { analytics } from "@lib/analytics";

export interface WebsiteChatProps {
  /** When true, the chat input is disabled and shows a muted state */
  locked?: boolean;
}

/**
 * Prompt shown above the chat input when build errors are detected.
 * Clicking "Fix errors" sends a message to the agent with the error details.
 */
function BuildErrorPrompt() {
  const consoleErrors = useConsoleErrors();
  const submit = useWebsiteChatSubmit();
  const isStreaming = useChatIsStreaming();
  const projectUuid = useProjectUuid();

  const errors = consoleErrors.filter((e) => e.type === "error");

  const handleFix = useCallback(() => {
    // The "Fix errors" button submits the raw stream payload directly, bypassing
    // useWebsiteComposer's formatSubmitWithActivePage — so attach the active page
    // route here too. Without it the ActivePageFocus reminder defaults to `/` and
    // points the agent at the index page while the error is on the page the user
    // is actually viewing.
    const activePageRoute = usePreviewStore.getState().route;
    submit({
      messages: [{ role: "human", content: "My page isn't displaying correctly, can you fix it?" }],
      consoleErrors: errors,
      ...(activePageRoute ? { activePageRoute } : {}),
    } as any);
    if (projectUuid)
      analytics.trackProject("website_edited", projectUuid, { edit_type: "error_fix" });
  }, [errors, submit, projectUuid]);

  if (errors.length === 0 || isStreaming) return null;

  return (
    <div
      data-testid="build-error-prompt"
      className="mb-2 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3 w-full"
    >
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-red-700">We ran into an issue building your page</p>
      </div>
      <button
        onClick={handleFix}
        className="shrink-0 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Fix errors
      </button>
    </div>
  );
}

/**
 * Decorate every recommendation submission (panel Send + prompt clicks)
 * the way the website composer's formatSubmit decorates normal sends:
 * attach the active page route so the ActivePageFocus middleware scopes
 * default edits to the page the user is looking at.
 */
function decorateWithActivePage(payload: Record<string, unknown>): Record<string, unknown> {
  const activePageRoute = usePreviewStore.getState().route;
  return activePageRoute ? { ...payload, activePageRoute } : payload;
}

/**
 * Website chat component following the same pattern as AdsChat.
 * Uses Chat compound components for consistent styling. Recommendations
 * are part of the chat widget: the chips row sits above the input, and an
 * active recommendation transforms the composer into its panel.
 */
export default function WebsiteChat() {
  // Use chat context for messages/streaming, domain hook for isLoadingHistory
  const isStreaming = useChatIsStreaming();
  const isLoadingHistory = useWebsiteChatIsLoadingHistory();
  const projectUuid = useProjectUuid();

  const decorateSubmit = useCallback(
    (payload: Record<string, unknown>) => {
      if (projectUuid) {
        analytics.trackProject("website_edited", projectUuid, {
          edit_type: "recommendation",
        });
      }
      return decorateWithActivePage(payload);
    },
    [projectUuid]
  );

  return (
    <Chat.Recommendations.Root
      renderers={RECOMMENDATION_PANELS}
      defaultSubmission={GenericSubmission}
      recommendations={recommendationsForSurface("website")}
      iconFor={recommendationIconFor}
      decorateSubmit={decorateSubmit}
      notePlaceholder="Add a note for Houston… (optional)"
    >
      <div
        className={twMerge(
          "bg-neutral-background rounded-b-2xl flex flex-col flex-1 min-h-0 justify-between",
        )}
        data-testid="website-chat"
        data-loading-history={isLoadingHistory}
        data-streaming={isStreaming}
      >
        <CardContent
          className="flex-1 px-4 py-4 min-h-0 flex flex-col"
          data-testid="website-chat-messages"
        >
          <WebsiteChatMessages />
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-2 px-4 pb-4 pt-0 w-full shrink-0">
          <BuildErrorPrompt />
          <Chat.Recommendations.Chips label="Suggested next steps" pageSize={6} />
          <Chat.Recommendations.Composer>
            <WebsiteChatInput />
          </Chat.Recommendations.Composer>
        </CardFooter>
      </div>
    </Chat.Recommendations.Root>
  );
}
